import React, { useContext, useState, useEffect, createContext } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from './firebase';
import { useMsal } from '@azure/msal-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
// Add this import:
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const AuthContext = createContext();
export function useAuth() {
  return useContext(AuthContext);
}

const ALL_SCOPES = [
  "User.Read.All",
  "Mail.ReadWrite",
  "Mail.ReadWrite.Shared",
  "MailboxSettings.ReadWrite",
  "Sites.Read.All",
  "Files.ReadWrite.All"
];

export function AuthProvider({ children }) {
  const { instance } = useMsal();

  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [officeUser, setOfficeUser] = useState(JSON.parse(localStorage.getItem('officeUser')));
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [username, setUsername] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(true);

  // 🔁 Load expected Microsoft username from Firestore
  useEffect(() => {
    const loadUsername = async () => {
      try {
        const ref = doc(db, 'serviceUsername', 'current');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setUsername(snap.data().username);
        } else {
          console.warn('No username found in /serviceUsername/current');
        }
      } catch (err) {
        console.error('Error loading username from Firestore:', err);
      } finally {
        setUsernameLoading(false); // ← Done loading, whether successful or not
      }
    };
    loadUsername();
  }, []);
  

  // 🔐 Firebase auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await ensureSettingsDoc();
        await refreshAccessToken();
      }
      setLoading(false);
    });
    return unsub;
    // eslint-disable-next-line
  }, [officeUser]);

  // 💾 Sync with localStorage
  useEffect(() => {
    if (officeUser) {
      localStorage.setItem('officeUser', JSON.stringify(officeUser));
    } else {
      localStorage.removeItem('officeUser');
    }
  }, [officeUser]);

  useEffect(() => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    } else {
      localStorage.removeItem('accessToken');
    }
  }, [accessToken]);

  // 🧠 Ensure /settings/{user} and optionally set /serviceUsername/current
  const ensureSettingsDoc = async () => {
    if (!officeUser) return;

    const settingsRef = doc(db, 'settings', officeUser.username);
    try {
      const snap = await getDoc(settingsRef);

      if (!snap.exists()) {
        const defaultSettings = {
          accessToken: accessToken || '',
          batchSize: 50,
          destinationMailFolder: {},
          displayName: currentUser?.displayName || officeUser.name || '',
          id: officeUser.username,
          path: '',
          frequency: 'daily',
          selectedMailboxFolders: [],
          selectedSharePointFolder: {},
          subscriptionIds: []
        };
        await setDoc(settingsRef, defaultSettings);
      }

      // ✅ Always check if serviceUsername is set — regardless of settings doc status
      const serviceRef = doc(db, 'serviceUsername', 'current');
      const serviceSnap = await getDoc(serviceRef);
      if (!serviceSnap.exists() && !username) {
        await setDoc(serviceRef, { username: officeUser.username });
        setUsername(officeUser.username);
        console.log('Service account set to:', officeUser.username);
      }

    } catch (error) {
      console.error('Error ensuring settings doc:', error);
    }
  };

  // 🔐 Firebase auth methods
  const login = (email, pass) => signInWithEmailAndPassword(auth, email, pass);
  const signup = (email, pass) => createUserWithEmailAndPassword(auth, email, pass);
  const logout = () => {
    setOfficeUser(null);
    setAccessToken(null);
    return signOut(auth);
  };

  // Helper: Get token, prompting user ONLY IF NEEDED
  const getTokenWithConsentIfNeeded = async (account) => {
    const request = { scopes: ALL_SCOPES, account };
    try {
      // 1. Try silently
      const silentResult = await instance.acquireTokenSilent(request);
      setAccessToken(silentResult.accessToken);
      return silentResult.accessToken;
    } catch (e) {
      // 2. Only prompt if interaction required
      if (e instanceof InteractionRequiredAuthError) {
        console.warn("Silent token failed, falling back to interactive consent");
        const popupResult = await instance.acquireTokenPopup({
          ...request,
          prompt: "consent" // only when needed
        });
        setAccessToken(popupResult.accessToken);
        return popupResult.accessToken;
      }
      // 3. Any other error
      console.error("Token Acquisition Error:", e);
      throw e;
    }
  };

  // 🎯 MSAL popup login using hint from Firestore
  const officeLogin = async () => {
    try {
      let loginHintUsername = username;
  
      // 🔁 Ensure username is loaded from Firestore
      if (!loginHintUsername) {
        const ref = doc(db, 'serviceUsername', 'current');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          loginHintUsername = snap.data().username;
          setUsername(loginHintUsername);
        } else {
          alert("Service account is not configured. Contact admin.");
          return;
        }
      }
  
      console.log("Attempting MS login with:", loginHintUsername);
  
      const loginResponse = await instance.loginPopup({
        scopes: ALL_SCOPES,
        loginHint: loginHintUsername,
        prompt: "login" // <-- Use 'login' to force re-auth with hint
      });
  
      const accountUsername = loginResponse.account.username;
  
      // ✅ Enforce match with expected username
      if (accountUsername !== loginHintUsername) {
        alert(`You must log in with ${loginHintUsername}. Instead, you used ${accountUsername}`);
        instance.logoutPopup({ account: loginResponse.account });
        return;
      }
  
      setOfficeUser(loginResponse.account);
      await getTokenWithConsentIfNeeded(loginResponse.account);
  
      // ✅ Optionally save username in Firestore if not already there
      const serviceRef = doc(db, 'serviceUsername', 'current');
      const snap = await getDoc(serviceRef);
      if (!snap.exists()) {
        await setDoc(serviceRef, { username: accountUsername });
      }
  
    } catch (error) {
      console.error("Office login error:", error);
    }
  };
  

  // ♻️ Refresh access token using MSAL cache
  const refreshAccessToken = async () => {
    if (!officeUser) return;
    return getTokenWithConsentIfNeeded(officeUser);
  };

  const officeLogout = () => {
    instance.logoutPopup();
    setOfficeUser(null);
    setAccessToken(null);
  };

  const value = {
    currentUser,
    login,
    signup,
    logout,
    officeUser,
    officeLogin,
    officeLogout,
    accessToken,
    refreshAccessToken,
    username,
    usernameLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
